import {useCallback, useDeferredValue, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {defaultRangeExtractor, useVirtualizer} from '@tanstack/react-virtual';
import {Alert, Button, Form, Spinner} from 'react-bootstrap';
import {getAllProperties} from '../service/PropertyService';
import PropertyEditorPanel from './PropertyEditorPanel';
import {readPropertyResponse} from './propertyEditorUtils';
import usePropertyWorkspaceLayout from './usePropertyWorkspaceLayout';
import './ManageProperty.css';

const SEARCH_FIELDS = ['property_id', 'full_address', 'cos_customer_name', 'agent_name', 'phone', 'email'];
const EMPTY_EDIT_STATE = {propertyId: null, dirty: false, busy: false};

export default function ManageProperty() {
    const [properties, setProperties] = useState([]);
    const [search, setSearch] = useState('');
    const deferredSearch = useDeferredValue(search);
    const [focusedRow, setFocusedRow] = useState(null);
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [pendingProperty, setPendingProperty] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [instructionType, setInstructionType] = useState('carpark');
    const [mobileView, setMobileView] = useState('list');
    const [editState, setEditState] = useState(EMPTY_EDIT_STATE);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [reload, setReload] = useState(0);
    const containerRef = useRef(null);
    const listRef = useRef(null);
    const searchRef = useRef(null);
    const detailRef = useRef(null);
    const editStateRef = useRef(EMPTY_EDIT_STATE);
    const focusRequest = useRef(null);
    const {height, isNarrow} = usePropertyWorkspaceLayout(containerRef);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError('');
        getAllProperties().then(response => readPropertyResponse(response, 'Unable to load properties.'))
            .then(rows => {
                if (!Array.isArray(rows)) throw new Error('Unable to read properties.');
                if (active) setProperties(rows);
            })
            .catch(loadError => { if (active) setError(loadError.message || 'Unable to load properties.'); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [reload]);

    const filtered = useMemo(() => {
        const query = deferredSearch.trim().toLowerCase();
        return properties.filter(property => !query || SEARCH_FIELDS.some(field =>
            String(property[field] ?? '').toLowerCase().includes(query)));
    }, [properties, deferredSearch]);
    const virtualizer = useVirtualizer({
        count: loading || error ? 0 : filtered.length,
        getScrollElement: () => listRef.current,
        getItemKey: index => filtered[index].property_id,
        estimateSize: () => 68,
        overscan: 6,
        // Keep the focused row and its neighbours mounted so Tab can traverse the full list.
        rangeExtractor: useCallback(range => {
            const indexes = defaultRangeExtractor(range);
            if (focusedRow != null) {
                for (let index = focusedRow - 1; index <= focusedRow + 1; index++) {
                    if (index >= 0 && index < range.count) indexes.push(index);
                }
            }
            return [...new Set(indexes)].sort((left, right) => left - right);
        }, [focusedRow]),
    });

    const receiveEditState = useCallback(next => {
        editStateRef.current = next;
        setEditState(current => current.propertyId === next.propertyId && current.dirty === next.dirty && current.busy === next.busy
            ? current : next);
    }, []);

    const selectProperty = useCallback(property => {
        receiveEditState({...EMPTY_EDIT_STATE, propertyId: property.property_id});
        setPendingProperty(null);
        setSelectedProperty(property);
        setMobileView('details');
    }, [receiveEditState]);

    useEffect(() => {
        if (!isNarrow && !selectedProperty && !loading && filtered.length > 0) selectProperty(filtered[0]);
    }, [isNarrow, selectedProperty, loading, filtered, selectProperty]);

    useEffect(() => {
        if (listRef.current) listRef.current.scrollTop = 0;
        setFocusedRow(null);
    }, [deferredSearch]);

    useLayoutEffect(() => {
        // A breakpoint change can hide the currently focused pane without unmounting it.
        if (isNarrow && mobileView === 'list' && detailRef.current?.contains(document.activeElement)) {
            focusRequest.current = 'list';
        } else if (isNarrow && mobileView === 'details'
            && containerRef.current?.querySelector('.manage-property-sidebar')?.contains(document.activeElement)) {
            focusRequest.current = 'details';
        }
        if (focusRequest.current === 'list' && (!isNarrow || mobileView === 'list')) {
            const selectedRow = listRef.current?.querySelector('[aria-current="true"]');
            (selectedRow || searchRef.current)?.focus({preventScroll: true});
            focusRequest.current = null;
        } else if (focusRequest.current === 'details' && (!isNarrow || mobileView === 'details')) {
            detailRef.current?.querySelector('h2')?.focus({preventScroll: true});
            focusRequest.current = null;
        }
    }, [selectedProperty?.property_id, mobileView, isNarrow, pendingProperty]);

    function requestProperty(property) {
        if (editStateRef.current.busy) return;
        if (selectedProperty?.property_id === property.property_id) {
            if (!isNarrow || mobileView === 'details') detailRef.current?.querySelector('h2')?.focus({preventScroll: true});
            else focusRequest.current = 'details';
            setMobileView('details');
            return;
        }
        if (editStateRef.current.dirty) {
            setPendingProperty(property);
            setMobileView('details');
            return;
        }
        if (isNarrow) focusRequest.current = 'details';
        selectProperty(property);
    }

    function backToProperties() {
        if (editStateRef.current.busy) return;
        focusRequest.current = 'list';
        setMobileView('list');
    }

    function saved(propertyId, key, value) {
        const patch = ['overview', 'guest-key'].includes(key) ? value : key === 'durations' ? {durations: value} : null;
        if (!patch) return;
        setProperties(current => current.map(property => property.property_id === propertyId ? {...property, ...patch} : property));
        setSelectedProperty(current => current?.property_id === propertyId ? {...current, ...patch} : current);
    }

    const navigationRequest = pendingProperty ? {
        id: `switch-${pendingProperty.property_id}`,
        title: 'Switch property?',
        message: `Switch to ${pendingProperty.full_address || `property #${pendingProperty.property_id}`}? Unsaved edits will be discarded. Saved changes and deleted attachments will remain.`,
        confirmLabel: 'Discard and switch',
        onConfirm: () => { focusRequest.current = 'details'; selectProperty(pendingProperty); },
        onCancel: () => setPendingProperty(null),
    } : null;

    return <main ref={containerRef} className="manage-property-page" data-mobile-view={mobileView} style={{height}}>
        <aside className="manage-property-sidebar" aria-labelledby="manage-property-title">
            <header className="manage-property-sidebar-header">
                <h1 id="manage-property-title">Property Management</h1>
                <Form.Control ref={searchRef} type="search" value={search} placeholder="Search"
                    aria-label="Search properties by address, ID, customer or contact"
                    onChange={event => setSearch(event.target.value)} />
            </header>
            <div ref={listRef} className="manage-property-list-scroll">
                {loading ? <div className="manage-property-status" role="status"><Spinner animation="border" size="sm" /><span>Loading properties…</span></div>
                    : error ? <Alert className="m-3" variant="danger"><div>{error}</div><Button className="mt-2" variant="outline-danger" onClick={() => setReload(current => current + 1)}>Retry</Button></Alert>
                        : <>
                            <ul className="manage-property-list" aria-label="Properties" style={{height: virtualizer.getTotalSize()}}>
                                {virtualizer.getVirtualItems().map(row => {
                                    const property = filtered[row.index];
                                    return <li key={row.key} className="manage-property-virtual-row"
                                        aria-posinset={row.index + 1} aria-setsize={filtered.length}
                                        style={{height: row.size, transform: `translateY(${row.start}px)`}}>
                                        <button type="button" className="manage-property-list-item"
                                            aria-current={selectedProperty?.property_id === property.property_id ? 'true' : undefined}
                                            aria-label={`${property.full_address || 'Address unavailable'}, #${property.property_id}, ${property.cos_customer_name || ''}`}
                                            title={`${property.full_address || ''} · #${property.property_id} · ${property.cos_customer_name || ''}`}
                                            onFocus={() => setFocusedRow(row.index)}
                                            disabled={editState.busy || Boolean(pendingProperty)} onClick={() => requestProperty(property)}>
                                            <span className="property-list-address">{property.full_address || 'Address unavailable'}</span>
                                            <span className="property-list-rooms">Bedroom: {property.bedroom || 0}, Bathroom: {property.bathroom || 0}, Sofabed: {property.sofabed || 0}</span>
                                            <span className="property-list-rooms">King: {property.king || 0}, Queen: {property.queen || 0}, Single: {property.single || 0}</span>
                                        </button>
                                    </li>;
                                })}
                            </ul>
                            {filtered.length === 0 ? <p className="manage-property-status" role="status">{search.trim() ? 'No properties match your search.' : 'No properties available.'}</p> : null}
                        </>}
            </div>
        </aside>
        <div ref={detailRef} className="manage-property-detail">
            {selectedProperty ? <PropertyEditorPanel key={selectedProperty.property_id} property={selectedProperty}
                enableJobCreation enableGuestKeyEditing
                activeTab={activeTab} onTabChange={setActiveTab} instructionType={instructionType} onInstructionTypeChange={setInstructionType}
                onSaved={saved} onEditStateChange={receiveEditState} navigationRequest={navigationRequest}
                headerAction={<Button className="property-back-button" variant="link" disabled={editState.busy}
                    onClick={backToProperties}>← Back to properties</Button>} />
                : <div className="manage-property-empty">{loading ? 'Loading properties…' : 'Select a property'}</div>}
        </div>
    </main>;
}
